import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRight,
  Shield,
  BarChart3,
  FileCheck,
  Users,
  CheckCircle,
  HandCoins,
  UserCheck,
  FileText,
  ClipboardCheck,
  Target,
  Camera,
  GitCompareArrows,
  ShieldCheck,
  Lock,
  Globe,
  Eye,
  Phone,
  Mail,
  MapPin,
  Menu,
  X
} from 'lucide-react';

function useScrollReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('revealed'); observer.unobserve(el); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} className={`reveal-on-scroll ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function LandingPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate({ admin: '/admin', agent: '/agent', donor: '/donor' }[user?.role] || '/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-zinc-200/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
            <img
              src="/verify.png"
              alt="Verified Digital Solutions"
              className="h-20 sm:h-48 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {['Features', 'How it Works', 'Why Verify'].map(t => (
              <a key={t} href={`#${t.toLowerCase().replace(/ /g, '-')}`} className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors font-medium">
                {t}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <button onClick={handleGetStarted} className="inline-flex items-center bg-[#002FA7] text-white hover:bg-[#002485] rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5" data-testid="go-to-dashboard-btn">
                Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline text-sm text-zinc-600 hover:text-zinc-900 font-medium transition-colors" data-testid="login-btn">Login</Link>
                <Link to="/register" className="hidden sm:inline-flex items-center bg-[#002FA7] text-white hover:bg-[#002485] rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-sm" data-testid="get-started-btn">
                  Get Started
                </Link>
              </>
            )}
            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-zinc-600 hover:text-zinc-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="landing-mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-100 px-6 py-4 space-y-3 animate-in slide-in-from-top-2">
            {['Features', 'How it Works', 'Why Verify'].map(t => (
              <a key={t} href={`#${t.toLowerCase().replace(/ /g, '-')}`} onClick={() => setMobileMenuOpen(false)} className="block text-sm text-zinc-600 hover:text-zinc-900 font-medium py-2">
                {t}
              </a>
            ))}
            <div className="border-t border-zinc-100 pt-3 flex flex-col gap-2">
              {isAuthenticated ? (
                <button onClick={() => { handleGetStarted(); setMobileMenuOpen(false); }} className="w-full bg-[#002FA7] text-white rounded-full px-5 py-2.5 text-sm font-medium">
                  Dashboard
                </button>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block text-center text-sm text-zinc-600 hover:text-zinc-900 font-medium py-2">
                    Login
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="block text-center bg-[#002FA7] text-white rounded-full px-5 py-2.5 text-sm font-medium">
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section className="pt-24 md:pt-32 pb-20 md:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Project Verification & Monitoring Platform
              </p>
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tighter leading-[1.08] text-zinc-950 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verify.
              </h1>
              <p className="text-lg sm:text-xl text-zinc-500 leading-relaxed mb-3 max-w-lg">
                Enterprise-grade monitoring and evaluation for organizations that demand transparency, accuracy, and real-world impact.
              </p>
              <p className="text-sm text-zinc-400 mb-10">
                A platform by <span className="font-medium text-zinc-500">Verified Digital Solutions</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={handleGetStarted} className="inline-flex items-center justify-center bg-[#002FA7] text-white hover:bg-[#002485] rounded-full px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-sm text-sm" data-testid="hero-cta-btn">
                  Get Started <ArrowRight className="w-4 h-4 ml-2" />
                </button>
                <button onClick={() => navigate('/register/donor')} className="inline-flex items-center justify-center bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 rounded-full px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-sm text-sm" data-testid="hero-submit-project-btn">
                  Submit a Project
                </button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1759922378275-32d7ca8bbcca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwcHJvZ3Jlc3MlMjBtb25pdG9yaW5nJTIwd29ya2VyJTIwc2FmZXR5JTIwdmVzdHxlbnwwfHx8fDE3NzY3NjQwNjl8MA&ixlib=rb-4.1.0&q=85"
                  alt="Field agents verifying project progress on site"
                  className="w-full h-[440px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/50 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-zinc-900">Verification Complete</p>
                        <p className="text-xs text-zinc-500">Water well project — indicators verified on site</p>
                      </div>
                      <div className="ml-auto">
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Consistent
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <section className="border-y border-zinc-100 bg-zinc-50/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '500+', label: 'Projects Monitored' },
              { value: '50+', label: 'Organizations' },
              { value: '1,000+', label: 'Field Reports' },
              { value: '99%', label: 'Verification Rate' }
            ].map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <p className="text-3xl md:text-4xl font-semibold text-zinc-950 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
                <p className="text-sm text-zinc-500 mt-1.5">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT IS VERIFY ── */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>What is Verify</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-zinc-950 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                One platform for complete project accountability
              </h2>
              <p className="text-lg text-zinc-500 leading-relaxed">
                Verify connects donors, administrators, and field agents in a single transparent workflow. Every indicator is tracked, every report is verified, and every result is backed by evidence.
              </p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: Lock, title: 'Immutable Records', desc: 'Every report, value, and decision is permanently recorded with timestamps and audit trails.' },
              { icon: Eye, title: 'Full Transparency', desc: 'Donors see verified results in real time. No hidden data, no guesswork — just facts.' },
              { icon: BarChart3, title: 'Real-Time Insights', desc: 'Live dashboards, comparison charts, and progress tracking across all your projects.' }
            ].map((f, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-white border border-zinc-200 rounded-2xl p-8 hover:shadow-md transition-shadow group">
                  <div className="w-11 h-11 bg-[#002FA7]/8 rounded-xl flex items-center justify-center mb-5">
                    <f.icon className="w-5 h-5 text-[#002FA7]" />
                  </div>
                  <h3 className="text-xl font-medium tracking-tight text-zinc-900 mb-2.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-24 md:py-32 bg-zinc-50/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>How it Works</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
                From submission to verified results
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: HandCoins, step: '01', title: 'Donor Submits', desc: 'Donor creates a project with goals, budget, and measurable indicators.' },
              { icon: Shield, step: '02', title: 'Admin Reviews', desc: 'Admin approves the project and selects single or double verification.' },
              { icon: UserCheck, step: '03', title: 'Agent Assigned', desc: 'One or two independent field agents are assigned to verify on the ground.' },
              { icon: FileText, step: '04', title: 'Reports Filed', desc: 'Agents submit evidence-backed reports with verified indicator values.' },
              { icon: CheckCircle, step: '05', title: 'Results Verified', desc: 'Admin reviews, compares, and releases final verified results to the donor.' }
            ].map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="relative bg-white border border-zinc-200 rounded-2xl p-6 hover:shadow-md transition-shadow h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#002FA7]/8 rounded-xl flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-5 h-5 text-[#002FA7]" />
                    </div>
                    <span className="text-xs font-bold text-zinc-300 tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>STEP {s.step}</span>
                  </div>
                  <h3 className="text-base font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES BENTO GRID ── */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Features</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-zinc-950" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Built for rigorous verification
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {/* Large card */}
            <Reveal className="md:col-span-8">
              <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow h-full">
                <div className="aspect-[16/9] bg-zinc-100 overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxkYXRhJTIwYW5hbHl0aWNzJTIwZGFzaGJvYXJkJTIwbW9uaXRvcmluZ3xlbnwwfHx8fDE3NzY3NjM1NzF8MA&ixlib=rb-4.1.0&q=85" alt="Dashboard analytics and comparison charts" className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Indicator Tracking & Comparison Charts</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">Track every KPI from donor targets to field-verified values. Side-by-side comparison charts make it easy to spot progress and discrepancies at a glance.</p>
                </div>
              </div>
            </Reveal>
            {/* Tall card */}
            <Reveal className="md:col-span-4" delay={100}>
              <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
                <div className="flex-1 bg-zinc-100 overflow-hidden min-h-[220px]">
                  <img src="https://images.unsplash.com/photo-1592209176240-e5b170e8474c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwZXJzb24lMjB1c2luZyUyMHRhYmxldCUyMHJlcG9ydCUyMGZpZWxkJTIwd29yayUyMGluc3BlY3Rpb258ZW58MHx8fHwxNzc2NzYzNTk2fDA&ixlib=rb-4.1.0&q=85" alt="Field agent using tablet for verification" className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Agent Verification</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">Independent field agents verify indicators with GPS, photos, and timestamped evidence.</p>
                </div>
              </div>
            </Reveal>
            {/* Bottom row */}
            {[
              { icon: Camera, title: 'Evidence Collection', desc: 'Photos, documents, and GPS coordinates attached to every field report.' },
              { icon: GitCompareArrows, title: 'Double Verification', desc: 'Two independent agents verify the same project for maximum credibility.' },
              { icon: Target, title: 'KPI Management', desc: 'Set measurable indicators, track targets, and compare planned vs actual results.' }
            ].map((f, i) => (
              <Reveal key={i} className="md:col-span-4" delay={i * 100}>
                <div className="bg-white border border-zinc-200 rounded-2xl p-8 hover:shadow-md transition-shadow h-full">
                  <div className="w-11 h-11 bg-[#002FA7]/8 rounded-xl flex items-center justify-center mb-5">
                    <f.icon className="w-5 h-5 text-[#002FA7]" />
                  </div>
                  <h3 className="text-base font-medium text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE VERIFY ── */}
      <section id="why-verify" className="py-24 md:py-32 bg-zinc-50/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-zinc-200">
                <img
                  src="https://images.pexels.com/photos/30678208/pexels-photo-30678208.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Healthcare worker performing checkup in clinic"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Why Verify</p>
              <h2 className="text-3xl sm:text-4xl font-medium tracking-tight leading-tight text-zinc-950 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Trusted by organizations that demand accountability
              </h2>
              <div className="space-y-6">
                {[
                  { icon: ShieldCheck, title: 'Uncompromised Integrity', desc: 'Independent double-agent verification ensures no single point of bias. Results you can stand behind.' },
                  { icon: Globe, title: 'Global Reach, Local Verification', desc: 'Field agents operate on the ground across regions, providing hyperlocal verification with global oversight.' },
                  { icon: Lock, title: 'Complete Data Security', desc: 'Role-based access control, anonymized donor reports, and encrypted data at every layer.' },
                  { icon: FileCheck, title: 'Audit-Ready Documentation', desc: 'Every verification cycle is preserved with full history. Export professional PDF reports at any time.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 bg-[#002FA7]/8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-5 h-5 text-[#002FA7]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-zinc-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h4>
                      <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-[#002FA7] rounded-3xl px-8 md:px-16 py-16 md:py-20 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.2) 0%, transparent 60%)' }} />
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Ready to verify your impact?
                </h2>
                <p className="text-blue-200 text-lg max-w-2xl mx-auto mb-10">
                  Join the organizations using Verify to build trust, ensure accountability, and deliver measurable results.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button onClick={handleGetStarted} className="inline-flex items-center justify-center bg-white text-[#002FA7] hover:bg-zinc-100 rounded-full px-7 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-sm text-sm" data-testid="cta-get-started-btn">
                    Get Started Today <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                  <button onClick={() => navigate('/register/donor')} className="inline-flex items-center justify-center bg-transparent text-white border border-white/30 hover:border-white/60 rounded-full px-7 py-3.5 font-medium transition-all text-sm" data-testid="cta-submit-project-btn">
                    Submit a Project
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-zinc-950 text-zinc-400 pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_80a6bcee-7228-4b60-b359-6ff952ed0f3c/artifacts/ufoinc0k_logo1-removebg-preview.png"
                  alt="Verified Digital Solutions"
                  className="h-40 w-auto brightness-0 invert"
                />
              </div>
              <p className="text-sm leading-relaxed mb-4">
                Building trust and transparency in development projects. Verify is the M&E platform by Verified Digital Solutions.
              </p>
              <p className="text-xs text-zinc-600">Powered by Verified Digital Solutions</p>
            </div>
            {/* Platform */}
            <div>
              <h4 className="text-white font-medium text-sm mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How it Works</a></li>
                <li><a href="#why-verify" className="hover:text-white transition-colors">Why Verify</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Get Started</Link></li>
              </ul>
            </div>
            {/* Company */}
            <div>
              <h4 className="text-white font-medium text-sm mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Company</h4>
              <ul className="space-y-3 text-sm">
                <li className="text-zinc-400">Verified Digital Solutions</li>
                <li className="text-zinc-400">Verify Platform</li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Support</a></li>
              </ul>
            </div>
            {/* Contact */}
            <div>
              <h4 className="text-white font-medium text-sm mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-zinc-600" />
                  <span>236-330-2588</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-zinc-600" />
                  <a href="mailto:info@verifieddigital.solutions" className="hover:text-white transition-colors">info@verifieddigital.solutions</a>
                </li>
                <li className="flex items-start gap-2.5">
                  <MapPin className="w-4 h-4 text-zinc-600 mt-0.5" />
                  <span>Vancouver, Canada</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-zinc-600">&copy; {new Date().getFullYear()} Verified Digital Solutions. All rights reserved.</p>
            <p className="text-zinc-600">Verify Platform</p>
          </div>
        </div>
      </footer>

      <style>{`
        .reveal-on-scroll {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-on-scroll.revealed {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
